"use client";

import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import "./MegaMenu.css";
import Image from "next/image";

export function MegaMenu({
  children,
  isActive,
  activeSectionIdx,
  setActiveSectionIdx,
  closeDesktopMenus,
}) {
  return (
    <div
      className={`megamenu-submenu megamenu-panel ${isActive ? "megamenu-panel--active" : ""} shadow-lg shadow-black/10 [clip-path:inset(0px_-100px_-200px_-100px)] fixed left-0 right-0 z-30 transition-all duration-350 ${isActive ? "active" : ""}`}
    >
      <div className="mx-auto bg-white border-t-1 border-gray-200">
        <div className="max-w-full mx-auto px-8 py-8">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-3 border-r border-gray-200 pr-6">
              <div className="space-y-1">
                {children.map((section, sIdx) => {
                  const isClickable = section.url && section.url !== "#";
                  if (section.url !== "" && section.url !== "#") {
                    return (
                      <Link
                        id={section.id}
                        key={section.id}
                        href={isClickable ? section.url : ""}
                        onMouseEnter={() => setActiveSectionIdx(sIdx)}
                        prefetch={false} 
                        onClick={(e) => {
                          if (!isClickable) {
                            e.preventDefault();
                          } else {
                            closeDesktopMenus();
                          }
                        }}
                        className={`py-2 px-3 rounded block transition-colors ${
                          isClickable ? "cursor-pointer" : "cursor-default"
                        } ${
                          activeSectionIdx === sIdx
                            ? "bg-blue-50 text-[#0475B3]"
                            : "hover:bg-blue-50"
                        }`}
                      >
                        <div className="font-semibold text-sm">{section.title}</div>
                      </Link>
                    );
                  }

                  return (
                    <div
                      id={section.id}
                      key={section.id}
                      onMouseEnter={() => setActiveSectionIdx(sIdx)}
                      className={`py-2 px-3 rounded block transition-colors cursor-pointer ${
                        activeSectionIdx === sIdx
                          ? "bg-blue-50 text-[#0475B3]"
                          : "hover:bg-blue-50"
                      }`}
                    >
                      <div className="font-semibold text-sm">{section.title}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="col-span-9">
              {children.map((section, sectionIndex) => {
                const currentSection = section;
                if (!currentSection) return null;

                if (currentSection.id === "international-full") {
                  const cards = currentSection.children || [];

                  const renderIntlCard = (card) => (
                    <div
                      key={card.id || card.title}
                      className="bg-[#E9F4FF] rounded-sm px-6 py-6 sm:px-8 sm:py-8 h-full flex flex-col"
                    >
                      {card.logo && (
                        <div className="mb-4">
                          <Image
                            src={card.logo}
                            alt={card.logoAlt || card.title}
                            width={160}
                            height={40}
                            className="h-8 sm:h-10 w-auto object-contain"
                            unoptimized
                          />
                        </div>
                      )}

                      <p className="text-base sm:text-lg font-semibold text-[#081E2D] mb-2">
                        {card.title}
                      </p>

                      {card.description && (
                        <p className="text-xs sm:text-sm text-gray-700 mb-4 leading-relaxed">
                          {card.description}
                        </p>
                      )}

                      {card.ctaUrl && (
                        <Link
                          href={card.ctaUrl}
                          className="inline-flex items-center text-xs sm:text-sm font-semibold text-[#0475B3] mb-4"
                          prefetch={false} 
                        >
                          {card.ctaLabel || "Know More"}
                          <ArrowUpRight className="ml-1 h-4 w-4" />
                        </Link>
                      )}

                      {card.sections?.map((section) => (
                        <div key={section.heading}>
                          <p className="text-xs sm:text-sm font-semibold text-[#0475B3] mb-2">
                            {section.heading}
                          </p>

                          <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm text-gray-800">
                            <div className="space-y-1">
                              {section.left?.map((it) => (
                                <div key={it.label} className="flex justify-between gap-2">
                                  <Link href={it.url || "#"} className="hover:text-[#0475B3]" prefetch={false} >
                                    {it.label}
                                  </Link>
                                  {it.value && <span className="text-gray-500">{it.value}</span>}
                                </div>
                              ))}
                            </div>

                            <div className="space-y-1">
                              {section.right?.map((it) => (
                                <div key={it.label} className="flex justify-between gap-2">
                                  <Link href={it.url || "#"} className="hover:text-[#0475B3]" prefetch={false} >
                                    {it.label}
                                  </Link>
                                  {it.value && <span className="text-gray-500">{it.value}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );

                  return (
                    <div
                      className={`mega-panel-content ${activeSectionIdx === sectionIndex ? "mega-panel-content--active" : ""} space-y-6`}
                      key={sectionIndex}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {cards.map(renderIntlCard)}
                      </div>
                    </div>
                  );
                }

                const sectionChildren = currentSection.children || [];
                const footerLinks = currentSection.footerNav || [];

                const col1 = sectionChildren.filter((child) => (child.column || 1) === 1);
                const col2 = sectionChildren.filter((child) => (child.column || 1) === 2);
                const col3 = sectionChildren.filter((child) => (child.column || 1) === 3);

                const hasSecondRow = sectionChildren.some((c) => c.row === 2);
                const ctaItems = sectionChildren.filter(
                  (c) => c.row === 3 || c.class === "explore-all",
                );

                const renderSchoolBlock = (school) => (
                  <div key={school.id || school.title} className="py-2 px-3">
                    {!school.class?.includes("hide-title") && (
                      <p
                        className={`font-bold text-[#0475B3] text-sm mb-3 pb-2 border-b border-blue-100 ${school.class || ""}`}
                      >
                        {school.url !== "" && school.url !== "#" ? (
                          <a href={school.url} target={school.target || "_self"}>
                            {school.title}
                          </a>
                        ) : (
                          <>{school.title}</>
                        )}
                      </p>
                    )}

                    <ul className="space-y-2">
                      {school.children?.map((child) => {
                        const hasGrandchildren =
                          Array.isArray(child.children) && child.children.length > 0;

                        return (
                          <li key={child.id || child.title}>
                            {hasGrandchildren ? (
                              <>
                                <div className="text-gray-700 text-sm font-medium">
                                  {child.title}
                                </div>

                                <ul className="ml-4 space-y-1">
                                  {child.children.map((grandchild) => (
                                    <li key={grandchild.id || grandchild.title} className="flex items-start">
                                      <span className="text-gray-400 mr-2 text-sm">•</span>
                                      <Link
                                        href={grandchild.url || "#"}
                                        target={grandchild.target || "_self"}
                                        className="text-gray-600 hover:text-[#0475B3] text-sm leading-relaxed"
                                        prefetch={false} 
                                      >
                                        {grandchild.title}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </>
                            ) : child.jslink ? (
                              <p
                                onClick={() => (window.location.href = child.url)}
                                className="text-gray-700 hover:text-[#0475B3] text-sm leading-relaxed block cursor-pointer"
                              >
                                {child.title}
                              </p>
                            ) : (
                              <Link
                                href={child.url || "#"}
                                target={child.target || "_self"}
                                className="text-gray-700 hover:text-[#0475B3] text-sm leading-relaxed block"
                                prefetch={false} 
                              >
                                {child.title}
                              </Link>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );

                const renderRow = (rowNum) => {
                  const rowCol1 = col1.filter((c) => (c.row || 1) === rowNum);
                  const rowCol2 = col2.filter((c) => (c.row || 1) === rowNum);
                  const rowCol3 = col3.filter((c) => (c.row || 1) === rowNum);

                  if (!rowCol1.length && !rowCol2.length && !rowCol3.length) return null;

                  return (
                    <div className="grid grid-cols-12 gap-8">
                      <div className="col-span-4">{rowCol1.map(renderSchoolBlock)}</div>
                      <div className="col-span-4">{rowCol2.map(renderSchoolBlock)}</div>
                      <div className="col-span-4">{rowCol3.map(renderSchoolBlock)}</div>
                    </div>
                  );
                };

                return (
                  <div
                    className={`mega-panel-content ${activeSectionIdx === sectionIndex ? "mega-panel-content--active" : ""} space-y-6`}
                    key={sectionIndex}
                  >
                    {renderRow(1)}

                    {hasSecondRow && (
                      <>
                        <hr className="border-t border-gray-200 my-4" />
                        {renderRow(2)}
                      </>
                    )}

                    {ctaItems.length > 0 && (
                      <div className="mt-4">
                        {ctaItems.map((cta) => (
                          <Link
                            key={cta.id || cta.title}
                            href={cta.url || "#"}
                            target={cta.target || "_self"}
                            prefetch={false} 
                            className={`inline-flex items-center w-fit bg-[#0475B31A] hover:bg-blue-100 text-black text-sm font-medium px-6 py-3 border border-blue-100 rounded-sm ${cta.class}`}
                          >
                            {cta.title}
                            <ArrowUpRight className="ml-2 h-4 w-4" />
                          </Link>
                        ))}
                      </div>
                    )}

                    {footerLinks.length > 0 && (
                      <div className="mt-6 pt-4 border-t mx-20 border-gray-200 flex flex-wrap justify-end gap-x-8 gap-y-2 text-xs font-medium text-gray-700">
                        {footerLinks.map((link) => (
                          <Link
                            key={link.id || link.title}
                            href={link.url || "#"}
                            target={link.target || "_self"}
                            className="hover:text-[#0475B3]"
                            prefetch={false} 
                          >
                            {link.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MegaMenu;
